<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import countries from 'shared/constants/countries';

const props = defineProps({
  contacts: { type: Array, required: true },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
  isLoadingMore: { type: Boolean, default: false },
  hasMore: { type: Boolean, default: true },
  activeSort: { type: String, default: '' },
  activeOrdering: { type: String, default: '' },
  // Incremented by parent on every full/fresh fetch — signals TableView to rebuild groups
  freshFetchKey: { type: Number, default: 0 },
});
const emit = defineEmits([
  'toggleContact',
  'toggleAll',
  'sort',
  'loadMore',
  'updateContact',
]);

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const { uiSettings, updateUISettings } = useUISettings();

const contactAttributes = useMapGetter('attributes/getContactAttributes');
const agents = useMapGetter('agents/getVerifiedAgents');
const store = useStore();

// Grouping state
// groupByColumn is always managed locally (from uiSettings), never from parent
const groupByColumn = ref(uiSettings.value?.contacts_table_group_by || '');
const collapsedGroups = ref(new Set());
const showGroupByDropdown = ref(false);
const groupBySearch = ref('');

// Agent assignment dropdown state
const agentDropdownContactId = ref(null);
const agentSearchQuery = ref('');
const agentDropdownStyle = ref({ top: '0px', left: '0px' });

// Format timestamp to YYYY-MM-DD HH:mm
const formatDateTime = timestamp => {
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const PLACEHOLDER = '—'; // Em dash placeholder
const ADD_ICON = '+'; // Plus icon for add column button

const visibleCustomColumns = ref(
  uiSettings.value?.contacts_table_columns || []
);

const showColumnDropdown = ref(false);
const attributeSearchQuery = ref('');

// Inline editing state
const editingCell = ref(null); // { contactId, field, value }
const editInputRef = ref(null);

// Infinite-scroll container and loading guard
const scrollContainer = ref(null);
const isFetchingMore = ref(false);

// Watch parent loading prop to release our local lock when parent finishes
watch(
  () => props.isLoadingMore,
  val => {
    if (!val) {
      // Delay release so DOM updates first and scroll position exits threshold
      nextTick(() => {
        isFetchingMore.value = false;
      });
    }
  }
);

// If hasMore becomes false, ensure scroll lock is released
watch(
  () => props.hasMore,
  val => {
    if (!val) {
      isFetchingMore.value = false;
    }
  }
);

// Reset search when dropdown closes
watch(showColumnDropdown, isOpen => {
  if (!isOpen) {
    attributeSearchQuery.value = '';
  }
});

const onScroll = () => {
  const el = scrollContainer.value;
  if (!el || isFetchingMore.value || !props.hasMore) return;
  const threshold = 50;
  if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
    isFetchingMore.value = true;
    emit('loadMore');
  }
};

onMounted(() => {
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener('scroll', onScroll, {
      passive: true,
    });
  }
  // Ensure agents are loaded for the assignment dropdown
  store.dispatch('agents/get');
});

onBeforeUnmount(() => {
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', onScroll);
  }
});

const selectedIdsSet = computed(() => new Set(props.selectedContactIds || []));

const countriesMap = computed(() => {
  return countries.reduce((acc, country) => {
    acc[country.id] = country;
    acc[country.name] = country;
    return acc;
  }, {});
});

// eslint-disable-next-line no-unused-vars
const availableCustomAttributes = computed(() => {
  const attrs = contactAttributes.value || [];
  return attrs.filter(
    attr => !visibleCustomColumns.value.includes(attr.attributeKey)
  );
});

const filteredAttributes = computed(() => {
  const attrs = contactAttributes.value || [];
  const query = attributeSearchQuery.value.toLowerCase().trim();

  if (!query) return attrs;

  return attrs.filter(
    attr =>
      attr.attributeDisplayName.toLowerCase().includes(query) ||
      attr.attributeKey.toLowerCase().includes(query)
  );
});

const isColumnVisible = attributeKey => {
  return visibleCustomColumns.value.includes(attributeKey);
};

const getAttributeDisplayName = key => {
  const attrs = contactAttributes.value || [];
  const attr = attrs.find(a => a.attributeKey === key);
  return attr?.attributeDisplayName || key;
};

const isSelected = id => selectedIdsSet.value.has(id);

const isAllSelected = computed(() => {
  if (props.contacts.length === 0) return false;
  return props.contacts.every(contact => selectedIdsSet.value.has(contact.id));
});

const isSomeSelected = computed(() => {
  if (props.contacts.length === 0) return false;
  return (
    props.contacts.some(contact => selectedIdsSet.value.has(contact.id)) &&
    !isAllSelected.value
  );
});

const handleSelectAll = checked => {
  emit('toggleAll', checked);
};

const onClickViewDetails = async id => {
  const routeTypes = {
    contacts_dashboard_segments_index: ['contacts_edit_segment', 'segmentId'],
    contacts_dashboard_labels_index: ['contacts_edit_label', 'label'],
  };
  const [name, paramKey] = routeTypes[route.name] || ['contacts_edit'];
  const params = {
    contactId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };
  await router.push({ name, params, query: route.query });
};

const getCountryDetails = additionalAttributes => {
  const attributes = additionalAttributes || {};
  // Support both snake_case (from API) and camelCase (Vue processed)
  const country = attributes.country;
  const countryCode = attributes.country_code || attributes.countryCode;
  const city = attributes.city;

  if (!country && !countryCode) return null;
  const activeCountry =
    countriesMap.value[country] ||
    (countryCode ? countriesMap.value[countryCode] : null) ||
    countriesMap.value.GB;
  if (!activeCountry) return null;
  return {
    countryCode: activeCountry.id,
    city: city || null,
    name: activeCountry.name,
  };
};

const formatLocation = additionalAttributes => {
  const details = getCountryDetails(additionalAttributes);
  if (!details) return '';
  return [details.city, details.name].filter(Boolean).join(', ');
};

// eslint-disable-next-line no-unused-vars
const handleSort = columnId => {
  // Determine new order based on current state
  let newOrder = 'asc';
  if (props.activeSort === columnId) {
    newOrder = props.activeOrdering === 'asc' ? 'desc' : 'asc';
  }

  // Emit to parent in the format it expects
  emit('sort', { sort: columnId, order: newOrder });
};

// eslint-disable-next-line no-unused-vars
const getSortIcon = columnId => {
  if (props.activeSort !== columnId) return 'i-lucide-arrow-up-down';
  return props.activeOrdering === 'asc'
    ? 'i-lucide-arrow-up'
    : 'i-lucide-arrow-down';
};

// Helper functions to access contact fields (snake_case from API)
const getPhoneNumber = contact => contact.phone_number || contact.phoneNumber;
const getAdditionalAttributes = contact =>
  contact.additional_attributes || contact.additionalAttributes;
const getLastActivityAt = contact =>
  contact.last_activity_at || contact.lastActivityAt;
const getLastCampaignAt = contact =>
  contact.last_campaign_at || contact.lastCampaignAt;
const getLastMessageAt = contact =>
  contact.last_message_at || contact.lastMessageAt;

// eslint-disable-next-line no-unused-vars
const isSorted = columnId => props.activeSort === columnId;

// eslint-disable-next-line no-unused-vars
const addCustomColumn = async attributeKey => {
  visibleCustomColumns.value = [attributeKey, ...visibleCustomColumns.value];
  showColumnDropdown.value = false;
  await updateUISettings({
    contacts_table_columns: visibleCustomColumns.value,
  });
};

const toggleCustomColumn = async attributeKey => {
  if (isColumnVisible(attributeKey)) {
    // Remove column
    visibleCustomColumns.value = visibleCustomColumns.value.filter(
      key => key !== attributeKey
    );
  } else {
    // Add column at the beginning (left side)
    visibleCustomColumns.value = [attributeKey, ...visibleCustomColumns.value];
  }

  await updateUISettings({
    contacts_table_columns: visibleCustomColumns.value,
  });
};

const removeCustomColumn = async attributeKey => {
  visibleCustomColumns.value = visibleCustomColumns.value.filter(
    key => key !== attributeKey
  );
  await updateUISettings({
    contacts_table_columns: visibleCustomColumns.value,
  });
};

const getCustomAttributeValue = (contact, attributeKey) => {
  const customAttrs = contact.custom_attributes || contact.customAttributes;

  if (!customAttrs || typeof customAttrs !== 'object') {
    return '-';
  }

  if (customAttrs[attributeKey] !== undefined) {
    return customAttrs[attributeKey] || '-';
  }

  return '-';
};

// Agent assignment helpers
const getAssignedAgentId = contact => {
  const customAttrs = contact.custom_attributes || contact.customAttributes;
  if (!customAttrs || typeof customAttrs !== 'object') return null;
  const val = customAttrs.assigned_agent_id;
  return val ? Number(val) : null;
};

const getAssignedAgent = contact => {
  const agentId = getAssignedAgentId(contact);
  if (!agentId) return null;
  return (agents.value || []).find(a => a.id === agentId) || null;
};

const getAssignedAgentIdById = contactId => {
  const contact = props.contacts.find(c => c.id === contactId);
  return contact ? getAssignedAgentId(contact) : null;
};

const filteredAgents = computed(() => {
  const list = agents.value || [];
  const query = agentSearchQuery.value.toLowerCase().trim();
  if (!query) return list;
  return list.filter(
    a =>
      a.name?.toLowerCase().includes(query) ||
      a.email?.toLowerCase().includes(query)
  );
});

const openAgentDropdown = (contactId, event) => {
  agentDropdownContactId.value = contactId;
  agentSearchQuery.value = '';
  const trigger = event.currentTarget;
  if (trigger) {
    const rect = trigger.getBoundingClientRect();
    agentDropdownStyle.value = {
      top: `${rect.bottom + 4}px`,
      left: `${rect.left}px`,
    };
  }
};

const closeAgentDropdown = () => {
  agentDropdownContactId.value = null;
  agentSearchQuery.value = '';
};

const assignAgent = (contactId, agentId) => {
  emit('updateContact', {
    contactId,
    field: 'assigned_agent_id',
    value: agentId ? String(agentId) : '',
  });
  closeAgentDropdown();
};

// Inline editing functions
const startEditing = (contactId, field, currentValue) => {
  // Don't start editing if the value is the placeholder "-"
  const actualValue = currentValue === '-' ? '' : currentValue;
  editingCell.value = { contactId, field, value: actualValue || '' };
  // Auto-focus input on next tick
  setTimeout(() => {
    const input = document.querySelector(
      'input[type="text"], input[type="email"], input[type="tel"]'
    );
    if (input) input.focus();
  }, 10);
};

const cancelEditing = () => {
  editingCell.value = null;
};

const saveEdit = () => {
  if (!editingCell.value) return;

  const { contactId, field, value } = editingCell.value;

  // Get original value for comparison
  const contact = props.contacts.find(c => c.id === contactId);
  let originalValue = '';

  if (field === 'name') {
    originalValue = contact?.name || '';
  } else if (field === 'email') {
    originalValue = contact?.email || '';
  } else if (field === 'phoneNumber') {
    originalValue = contact?.phoneNumber || '';
  } else {
    originalValue = getCustomAttributeValue(contact, field);
    if (originalValue === '-') originalValue = '';
  }

  // Trim the new value
  const trimmedValue = (value || '').trim();

  // Don't save if value hasn't changed
  if (trimmedValue === originalValue) {
    editingCell.value = null;
    return;
  }

  // Only emit if there's an actual change
  emit('updateContact', {
    contactId,
    field,
    value: trimmedValue,
  });

  editingCell.value = null;
};

const isEditing = (contactId, field) => {
  return (
    editingCell.value?.contactId === contactId &&
    editingCell.value?.field === field
  );
};

// Column resize functionality
const columnWidths = ref({
  checkbox: 40,
  name: 200,
  email: 200,
  phone: 140,
  location: 180,
  lastActivity: 150,
  lastMessage: 150,
  lastCampaign: 180,
});

const resizing = ref(null);
const resizeStartX = ref(0);
const resizeStartWidth = ref(0);

const onResize = event => {
  if (!resizing.value) return;
  const diff = event.clientX - resizeStartX.value;
  const newWidth = Math.max(60, resizeStartWidth.value + diff);
  columnWidths.value[resizing.value] = newWidth;
};

const stopResize = () => {
  resizing.value = null;
  document.removeEventListener('mousemove', onResize);
  document.removeEventListener('mouseup', stopResize);
  document.body.classList.remove('cursor-col-resize', 'select-none');
};

// eslint-disable-next-line no-unused-vars
const startResize = (event, columnKey) => {
  event.preventDefault();
  event.stopPropagation();
  resizing.value = columnKey;
  resizeStartX.value = event.clientX;
  resizeStartWidth.value = columnWidths.value[columnKey];
  document.addEventListener('mousemove', onResize);
  document.addEventListener('mouseup', stopResize);
  document.body.classList.add('cursor-col-resize', 'select-none');
};
// Groupable column definitions
const GROUPABLE_COLUMNS = computed(() => [
  { key: '', label: t('CONTACTS_LAYOUT.TABLE.GROUP_BY.NONE') },
  { key: 'location', label: t('CONTACTS_LAYOUT.TABLE.LOCATION') },
  {
    key: 'email_domain',
    label: t('CONTACTS_LAYOUT.TABLE.GROUP_BY.EMAIL_DOMAIN'),
  },
  ...(contactAttributes.value || []).map(attr => ({
    key: `custom:${attr.attributeKey}`,
    label: attr.attributeDisplayName,
  })),
]);

const filteredGroupableColumns = computed(() => {
  const q = groupBySearch.value.trim().toLowerCase();
  if (!q) return GROUPABLE_COLUMNS.value;
  return GROUPABLE_COLUMNS.value.filter(col =>
    col.label.toLowerCase().includes(q)
  );
});

const getGroupKey = contact => {
  const col = groupByColumn.value;
  if (!col) return null;

  if (col === 'location') {
    return (
      formatLocation(getAdditionalAttributes(contact)) ||
      t('CONTACTS_LAYOUT.TABLE.GROUP_BY.UNKNOWN')
    );
  }
  if (col === 'email_domain') {
    const email = contact.email || '';
    const domain = email.includes('@') ? email.split('@')[1] : '';
    return domain || t('CONTACTS_LAYOUT.TABLE.GROUP_BY.UNKNOWN');
  }
  if (col.startsWith('custom:')) {
    const attrKey = col.replace('custom:', '');
    const val = getCustomAttributeValue(contact, attrKey);
    return val && val !== '-'
      ? String(val)
      : t('CONTACTS_LAYOUT.TABLE.GROUP_BY.UNKNOWN');
  }
  return t('CONTACTS_LAYOUT.TABLE.GROUP_BY.UNKNOWN');
};

// Build groups as a pure computed — always in sync with props.contacts
// No manual watchers, no mutable local state, no stale data on scroll append
const groupedContacts = computed(() => {
  if (!groupByColumn.value) return null;
  const unknownLabel = t('CONTACTS_LAYOUT.TABLE.GROUP_BY.UNKNOWN');
  const map = new Map();
  props.contacts.forEach(contact => {
    const key = getGroupKey(contact);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(contact);
  });
  const entries = [...map.entries()].sort((a, b) => {
    if (a[0] === unknownLabel) return 1;
    if (b[0] === unknownLabel) return -1;
    return a[0].localeCompare(b[0]);
  });
  return entries.map(([label, items]) => ({ label, contacts: items }));
});

// Reset collapsed groups on fresh fetch (new data set)
watch(
  () => props.freshFetchKey,
  () => {
    collapsedGroups.value = new Set();
  }
);

const toggleGroupCollapse = label => {
  const next = new Set(collapsedGroups.value);
  if (next.has(label)) {
    next.delete(label);
  } else {
    next.add(label);
  }
  collapsedGroups.value = next;
};

const isGroupCollapsed = label => collapsedGroups.value.has(label);

const allGroupsCollapsed = computed(() => {
  if (!groupedContacts.value) return false;
  return groupedContacts.value.every(g => collapsedGroups.value.has(g.label));
});

const toggleAllGroups = () => {
  if (allGroupsCollapsed.value) {
    collapsedGroups.value = new Set();
  } else {
    const next = new Set();
    groupedContacts.value.forEach(g => next.add(g.label));
    collapsedGroups.value = next;
  }
};

const setGroupBy = async key => {
  groupByColumn.value = key;
  collapsedGroups.value = new Set();
  showGroupByDropdown.value = false;
  groupBySearch.value = '';
  await updateUISettings({ contacts_table_group_by: key });
};

const groupByLabel = computed(() => {
  if (!groupByColumn.value) return t('CONTACTS_LAYOUT.TABLE.GROUP_BY.LABEL');
  const col = GROUPABLE_COLUMNS.value.find(c => c.key === groupByColumn.value);
  return col ? col.label : groupByColumn.value;
});

const getGroupByIcon = key => {
  if (!key) return 'i-lucide-layers-2';
  if (key === 'location') return 'i-lucide-map-pin';
  if (key === 'email_domain') return 'i-lucide-at-sign';
  if (key.startsWith('custom:')) return 'i-lucide-tag';
  return 'i-lucide-layers';
};

// No accent colors needed - clean minimal design

const totalColumns = computed(() => {
  // checkbox + name + email + phone + location + lastActivity + lastMessage + lastCampaign + assignedAgent + custom cols + actions + add-col
  return 9 + visibleCustomColumns.value.length + 2;
});

// Flat list of display rows: either plain contacts or grouped with headers
const displayRows = computed(() => {
  if (!groupedContacts.value) {
    return props.contacts.map(c => ({ type: 'contact', data: c }));
  }

  const rows = [];
  groupedContacts.value.forEach(grp => {
    rows.push({ type: 'group', label: grp.label, count: grp.contacts.length });
    if (!isGroupCollapsed(grp.label)) {
      grp.contacts.forEach(c =>
        rows.push({ type: 'contact', data: c, groupLabel: grp.label })
      );
    }
  });
  return rows;
});

// IDs in current visual order (used for shift-range selection)
const visualOrderIds = computed(() =>
  displayRows.value.filter(r => r.type === 'contact').map(r => r.data.id)
);

const handleSelect = (id, checked, event) => {
  emit('toggleContact', {
    id,
    value: checked,
    isShiftKey: event?.shiftKey || false,
    orderedIds: visualOrderIds.value,
  });
};
</script>

<template>
  <div
    ref="scrollContainer"
    class="w-full relative overflow-auto max-h-[calc(100vh-200px)] rounded-lg border border-n-slate-4"
  >
    <!-- Active group-by indicator bar -->
    <div
      v-if="groupByColumn"
      class="flex items-center gap-2 px-4 py-1.5 bg-n-slate-2 border-b border-n-slate-4 sticky top-0 left-0 z-[101] w-fit min-w-full"
    >
      <Icon
        icon="i-lucide-layers"
        class="size-3.5 text-n-slate-11 flex-shrink-0"
      />
      <span class="text-xs font-medium text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.TABLE.GROUP_BY.GROUPED_BY') }}
      </span>
      <span class="text-xs font-semibold text-n-slate-12">
        {{ groupByLabel }}
      </span>
      <span class="text-[11px] text-n-slate-10 tabular-nums">
        {{
          `· ${groupedContacts?.length || 0} ${
            (groupedContacts?.length || 0) === 1
              ? t('CONTACTS_LAYOUT.TABLE.GROUP_BY.GROUP_SINGULAR')
              : t('CONTACTS_LAYOUT.TABLE.GROUP_BY.GROUP_PLURAL')
          }`
        }}
      </span>
      <div class="flex items-center gap-0.5 ml-auto">
        <button
          class="flex items-center gap-1 text-[11px] text-n-slate-10 hover:text-n-slate-12 transition-colors px-1.5 py-0.5 rounded hover:bg-n-slate-3"
          @click="toggleAllGroups"
        >
          <Icon
            :icon="
              allGroupsCollapsed
                ? 'i-lucide-unfold-vertical'
                : 'i-lucide-fold-vertical'
            "
            class="size-3"
          />
          {{
            allGroupsCollapsed
              ? t('CONTACTS_LAYOUT.TABLE.GROUP_BY.EXPAND_ALL')
              : t('CONTACTS_LAYOUT.TABLE.GROUP_BY.COLLAPSE_ALL')
          }}
        </button>
        <button
          class="flex items-center text-[11px] text-n-slate-10 hover:text-n-slate-12 transition-colors p-0.5 rounded hover:bg-n-slate-3"
          @click="setGroupBy('')"
        >
          <Icon icon="i-lucide-x" class="size-3.5" />
        </button>
      </div>
    </div>

    <table class="w-full table-fixed border-collapse">
      <thead
        class="bg-n-slate-1 sticky z-[100] border-b border-n-slate-4"
        :class="groupByColumn ? 'top-[33px]' : 'top-0'"
      >
        <tr>
          <th
            class="w-[36px] min-w-[36px] max-w-[36px] text-left py-1 pl-2 pr-0 font-semibold text-xs uppercase tracking-wider text-n-slate-11 sticky left-0 bg-n-slate-1 z-[111]"
          >
            <div class="flex items-center justify-center">
              <Checkbox
                :model-value="isAllSelected"
                :indeterminate="isSomeSelected"
                @change="e => handleSelectAll(e.target.checked)"
              />
            </div>
          </th>

          <th
            class="text-left py-1 pl-0 pr-2 font-semibold text-xs uppercase tracking-wider text-n-slate-11 sticky left-[36px] bg-n-slate-1 z-[110] border-r border-n-slate-3"
            :style="{
              width: columnWidths.name + 'px',
              minWidth: columnWidths.name + 'px',
            }"
          >
            {{ t('CONTACTS_LAYOUT.TABLE.NAME') }}
            <div
              class="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-n-brand/50 transition-colors"
              @mousedown="e => startResize(e, 'name')"
            />
          </th>

          <th
            class="text-left py-1 px-2 font-semibold text-xs uppercase tracking-wider text-n-slate-11 bg-n-slate-1 relative z-[90] border-r border-n-slate-3"
            :style="{
              width: columnWidths.email + 'px',
              minWidth: columnWidths.email + 'px',
            }"
          >
            {{ t('CONTACTS_LAYOUT.TABLE.EMAIL') }}
            <div
              class="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-n-brand/50 transition-colors"
              @mousedown="e => startResize(e, 'email')"
            />
          </th>

          <th
            class="text-left py-1 px-2 font-semibold text-xs uppercase tracking-wider text-n-slate-11 bg-n-slate-1 relative z-[90] border-r border-n-slate-3"
            :style="{
              width: columnWidths.phone + 'px',
              minWidth: columnWidths.phone + 'px',
            }"
          >
            {{ t('CONTACTS_LAYOUT.TABLE.PHONE') }}
            <div
              class="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-n-brand/50 transition-colors"
              @mousedown="e => startResize(e, 'phone')"
            />
          </th>

          <th
            class="text-left py-1 px-2 font-semibold text-xs uppercase tracking-wider text-n-slate-11 bg-n-slate-1 relative z-[90] border-r border-n-slate-3"
            :style="{
              width: columnWidths.location + 'px',
              minWidth: columnWidths.location + 'px',
            }"
          >
            {{ t('CONTACTS_LAYOUT.TABLE.LOCATION') }}
            <div
              class="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-n-brand/50 transition-colors"
              @mousedown="e => startResize(e, 'location')"
            />
          </th>

          <th
            class="text-left py-1 px-2 font-semibold text-xs uppercase tracking-wider text-n-slate-11 bg-n-slate-1 relative z-[90] border-r border-n-slate-3"
            :style="{
              width: columnWidths.lastActivity + 'px',
              minWidth: columnWidths.lastActivity + 'px',
            }"
          >
            {{ t('CONTACTS_LAYOUT.TABLE.LAST_ACTIVITY') }}
            <div
              class="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-n-brand/50 transition-colors"
              @mousedown="e => startResize(e, 'lastActivity')"
            />
          </th>

          <th
            class="text-left py-1 px-2 font-semibold text-xs uppercase tracking-wider text-n-slate-11 bg-n-slate-1 relative z-[90] border-r border-n-slate-3"
            :style="{
              width: columnWidths.lastMessage + 'px',
              minWidth: columnWidths.lastMessage + 'px',
            }"
          >
            {{ t('CONTACTS_LAYOUT.TABLE.LAST_MESSAGE') }}
            <div
              class="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-n-brand/50 transition-colors"
              @mousedown="e => startResize(e, 'lastMessage')"
            />
          </th>

          <th
            class="text-left py-1 px-2 font-semibold text-xs uppercase tracking-wider text-n-slate-11 bg-n-slate-1 relative z-[90] border-r border-n-slate-3"
            :style="{
              width: columnWidths.lastCampaign + 'px',
              minWidth: columnWidths.lastCampaign + 'px',
            }"
          >
            {{ t('CONTACTS_LAYOUT.TABLE.LAST_CAMPAIGN') }}
            <div
              class="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-n-brand/50 transition-colors"
              @mousedown="e => startResize(e, 'lastCampaign')"
            />
          </th>

          <th
            class="w-[160px] text-left py-1 px-2 font-semibold text-xs uppercase tracking-wider text-n-slate-11 bg-n-slate-1 relative z-[90] border-r border-n-slate-3"
          >
            {{ t('CONTACTS_LAYOUT.TABLE.ASSIGNED_AGENT') }}
          </th>

          <th
            v-for="attrKey in visibleCustomColumns"
            :key="attrKey"
            class="w-[150px] text-left py-1 px-2 font-semibold text-xs uppercase tracking-wider text-n-slate-11 bg-n-slate-1 relative z-[90] border-r border-n-slate-3"
          >
            <div class="flex items-center gap-1 group">
              <span>{{ getAttributeDisplayName(attrKey) }}</span>
              <button
                class="opacity-0 group-hover:opacity-100 text-n-slate-9 hover:text-n-ruby-9 transition-opacity"
                @click="removeCustomColumn(attrKey)"
              >
                <Icon icon="i-lucide-x" class="size-3.5" />
              </button>
            </div>
          </th>

          <th
            class="w-[110px] text-left py-1 px-2 font-semibold text-xs text-n-slate-11 bg-n-slate-1 relative z-[90]"
          />

          <th
            class="py-1 px-2 w-[56px] bg-n-slate-1 sticky right-0 z-[110] shadow-[-4px_0_8px_rgba(0,0,0,0.05)]"
          >
            <div class="relative flex items-center gap-1">
              <!-- Group by button (span to avoid global button CSS) -->
              <span
                role="button"
                tabindex="0"
                class="inline-flex items-center justify-center w-5 h-5 rounded-lg bg-n-brand hover:brightness-110 text-white cursor-pointer"
                :class="
                  groupByColumn ? 'ring-2 ring-offset-1 ring-n-brand' : ''
                "
                :title="t('CONTACTS_LAYOUT.TABLE.GROUP_BY.LABEL')"
                @click.stop="showGroupByDropdown = !showGroupByDropdown"
                @keydown.enter.stop="showGroupByDropdown = !showGroupByDropdown"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M12 2 2 7l10 5 10-5-10-5Z" />
                  <path d="m2 17 10 5 10-5" />
                  <path d="m2 12 10 5 10-5" />
                </svg>
              </span>
              <!-- Group by overlay -->
              <div
                v-if="showGroupByDropdown"
                class="fixed inset-0 z-40"
                @click="showGroupByDropdown = false"
              />
              <!-- Group by dropdown -->
              <div
                v-if="showGroupByDropdown"
                class="absolute right-0 top-full mt-1 w-52 bg-n-solid-2 rounded-lg shadow-lg border border-n-slate-4 z-50 overflow-hidden"
              >
                <!-- Search bar -->
                <div class="px-2 pt-2 pb-1 border-b border-n-slate-4">
                  <input
                    v-model="groupBySearch"
                    type="text"
                    :placeholder="t('CONTACTS_LAYOUT.TABLE.GROUP_BY.SEARCH')"
                    class="w-full text-[12px] px-2 py-1 rounded bg-n-solid-1 border border-n-slate-4 text-n-slate-12 placeholder-n-slate-9 outline-none focus:border-n-brand"
                    @click.stop
                  />
                </div>
                <div class="py-1 max-h-64 overflow-y-auto">
                  <button
                    v-for="col in filteredGroupableColumns"
                    :key="col.key"
                    type="button"
                    class="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] transition-colors"
                    :class="
                      groupByColumn === col.key
                        ? 'bg-n-slate-3 text-n-slate-12 font-medium'
                        : 'text-n-slate-11 hover:bg-n-slate-2 hover:text-n-slate-12'
                    "
                    @click="setGroupBy(col.key)"
                  >
                    <Icon
                      :icon="getGroupByIcon(col.key)"
                      class="size-3.5 flex-shrink-0 text-n-slate-10"
                    />
                    <span class="flex-1 text-left">{{ col.label }}</span>
                    <Icon
                      v-if="groupByColumn === col.key"
                      icon="i-lucide-check"
                      class="size-3.5 text-n-slate-12 flex-shrink-0"
                    />
                  </button>
                </div>
              </div>
              <!-- Add column button (span to avoid global button CSS) -->
              <span
                role="button"
                tabindex="0"
                class="inline-flex items-center justify-center w-5 h-5 rounded-lg bg-n-brand hover:brightness-110 text-white cursor-pointer font-bold text-sm leading-none"
                :title="t('CONTACTS_LAYOUT.TABLE.ADD_COLUMN')"
                @click.stop="showColumnDropdown = !showColumnDropdown"
                @keydown.enter.stop="showColumnDropdown = !showColumnDropdown"
              >
                {{ ADD_ICON }}
              </span>
              <!-- Overlay for closing dropdown -->
              <div
                v-if="showColumnDropdown"
                class="fixed inset-0 z-40"
                @click="showColumnDropdown = false"
              />
              <!-- Dropdown menu -->
              <div
                v-if="showColumnDropdown"
                class="absolute right-0 top-full mt-2 w-72 bg-n-solid-2 rounded-xl shadow-xl border border-n-weak z-50 overflow-hidden"
              >
                <!-- Header -->
                <div class="px-4 py-3 border-b border-n-weak">
                  <h3 class="text-sm font-semibold text-n-slate-12">
                    {{ t('CONTACTS_LAYOUT.TABLE.CUSTOM_ATTRIBUTES') }}
                  </h3>
                </div>

                <!-- Search bar -->
                <div class="px-3 py-2 border-b border-n-weak">
                  <input
                    v-model="attributeSearchQuery"
                    type="text"
                    :placeholder="
                      t('CONTACTS_LAYOUT.TABLE.SEARCH_ATTRIBUTES_PLACEHOLDER')
                    "
                    class="reset-base no-margin w-full px-3 py-2 text-sm bg-n-solid-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-11 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent !h-auto !m-0 !mb-0"
                  />
                </div>

                <!-- Attributes list -->
                <div class="max-h-64 overflow-y-auto py-1">
                  <template v-if="filteredAttributes.length > 0">
                    <button
                      v-for="attr in filteredAttributes"
                      :key="attr.attributeKey"
                      type="button"
                      class="w-full flex items-center justify-between px-4 py-2.5 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors group"
                      @click="toggleCustomColumn(attr.attributeKey)"
                    >
                      <span class="flex-1 text-left">{{
                        attr.attributeDisplayName
                      }}</span>
                      <Icon
                        v-if="isColumnVisible(attr.attributeKey)"
                        icon="i-lucide-check"
                        class="size-4 text-n-brand flex-shrink-0"
                      />
                    </button>
                  </template>
                  <div
                    v-else-if="attributeSearchQuery"
                    class="px-4 py-6 text-sm text-n-slate-11 text-center"
                  >
                    {{ t('CONTACTS_LAYOUT.TABLE.NO_ATTRIBUTES_FOUND') }}
                  </div>
                  <div v-else class="px-4 py-6 text-center">
                    <p class="text-sm text-n-slate-11 mb-3">
                      {{ t('CONTACTS_LAYOUT.TABLE.NO_CUSTOM_ATTRIBUTES') }}
                    </p>
                    <a
                      href="/app/accounts/1/settings/custom-attributes/list"
                      class="text-sm text-n-brand hover:underline inline-flex items-center gap-1"
                    >
                      {{ t('CONTACTS_LAYOUT.TABLE.CREATE_CUSTOM_ATTRIBUTE') }}
                      <Icon icon="i-lucide-arrow-right" class="size-3" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </th>
        </tr>
      </thead>

      <tbody>
        <template
          v-for="item in displayRows"
          :key="item.type === 'group' ? `g-${item.label}` : item.data.id"
        >
          <!-- Group header row -->
          <tr
            v-if="item.type === 'group'"
            class="cursor-pointer select-none bg-n-slate-3 hover:bg-n-slate-4 transition-colors sticky z-[60]"
            :style="{ top: groupByColumn ? '66px' : '33px' }"
            @click="toggleGroupCollapse(item.label)"
          >
            <!-- Sticky label cell spans first 2 sticky columns (checkbox + name) -->
            <td
              class="border-b border-n-slate-4 py-2 pl-3 pr-0 sticky left-0 z-[61] bg-n-slate-3 hover:bg-n-slate-4 w-[36px] min-w-[36px] max-w-[36px]"
            >
              <Icon
                :icon="
                  isGroupCollapsed(item.label)
                    ? 'i-lucide-chevron-right'
                    : 'i-lucide-chevron-down'
                "
                class="size-3.5 flex-shrink-0 text-n-slate-10"
              />
            </td>
            <td
              class="border-b border-n-slate-4 py-2 pl-0 pr-4 sticky left-[36px] z-[61] bg-n-slate-3 hover:bg-n-slate-4"
            >
              <div class="flex items-center gap-2 whitespace-nowrap">
                <span class="text-xs font-semibold text-n-slate-12">
                  {{ item.label }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{
                    item.count === 1
                      ? `· 1 ${t('CONTACTS_LAYOUT.TABLE.GROUP_BY.CONTACT_COUNT')}`
                      : `· ${item.count} ${t('CONTACTS_LAYOUT.TABLE.GROUP_BY.CONTACTS_COUNT')}`
                  }}
                </span>
                <Icon
                  v-if="isGroupCollapsed(item.label)"
                  icon="i-lucide-eye-off"
                  class="size-3 text-n-slate-9 flex-shrink-0"
                />
              </div>
            </td>
            <!-- Empty cells for remaining columns -->
            <td
              :colspan="totalColumns - 2"
              class="border-b border-n-slate-4 bg-n-slate-3"
            />
          </tr>
          <!-- Contact row -->
          <tr
            v-else
            class="transition-colors duration-100 cursor-pointer border-b border-n-slate-3 hover:bg-n-alpha-2"
            :class="
              isSelected(item.data.id)
                ? 'bg-woot-25 dark:bg-woot-800/30 hover:bg-woot-50 dark:hover:bg-woot-800/50'
                : 'bg-n-solid-1'
            "
          >
            <td
              class="py-px pl-2 pr-0 w-[36px] min-w-[36px] max-w-[36px] sticky left-0 z-[51] transition-colors"
              :class="
                isSelected(item.data.id)
                  ? 'bg-woot-25 dark:bg-woot-800/30'
                  : 'bg-n-solid-1'
              "
            >
              <div
                class="flex items-center justify-center cursor-pointer"
                @click.stop="
                  e => handleSelect(item.data.id, !isSelected(item.data.id), e)
                "
              >
                <Checkbox
                  class="pointer-events-none"
                  :model-value="isSelected(item.data.id)"
                />
              </div>
            </td>

            <td
              class="py-px pl-0 pr-2 sticky left-[36px] z-[50] border-r border-n-slate-3 group/cell transition-colors"
              :class="
                isSelected(item.data.id)
                  ? 'bg-woot-25 dark:bg-woot-800/30'
                  : 'bg-n-solid-1'
              "
            >
              <div class="flex items-center gap-1.5">
                <Avatar
                  :name="item.data.name"
                  :src="item.data.thumbnail"
                  :size="18"
                  :status="item.data.availabilityStatus"
                  hide-offline-status
                  rounded-full
                />
                <div class="min-w-0 flex-1">
                  <div
                    v-if="!isEditing(item.data.id, 'name')"
                    class="flex items-center gap-1 cursor-text h-[22px]"
                    @dblclick="
                      startEditing(item.data.id, 'name', item.data.name)
                    "
                  >
                    <span
                      class="font-medium text-xs text-n-slate-12 truncate flex-1 leading-[22px]"
                    >
                      {{ item.data.name }}
                    </span>
                  </div>
                  <div v-else class="h-[22px]" @click.stop>
                    <input
                      ref="editInputRef"
                      v-model="editingCell.value"
                      type="text"
                      class="reset-base no-margin !h-[22px] !m-0 !mb-0 !p-0 !px-1 !py-0 !border !border-solid !border-n-brand !rounded !outline-none !bg-n-solid-1 !text-n-slate-12 !text-xs !leading-[20px] !block !w-full box-border !appearance-none"
                      @keydown.enter="saveEdit"
                      @keydown.esc="cancelEditing"
                      @keydown.tab="saveEdit"
                      @blur="saveEdit"
                    />
                  </div>
                </div>
              </div>
            </td>

            <td
              class="py-px px-2 relative z-0 border-r border-n-slate-3 group/cell transition-colors"
            >
              <div
                v-if="!isEditing(item.data.id, 'email')"
                class="cursor-text h-[22px] flex items-center"
                @dblclick="startEditing(item.data.id, 'email', item.data.email)"
              >
                <span
                  class="text-xs text-n-slate-11 truncate block leading-[22px]"
                  :title="item.data.email"
                >
                  {{ item.data.email || '-' }}
                </span>
              </div>
              <div v-else class="h-[22px]" @click.stop>
                <input
                  v-model="editingCell.value"
                  type="email"
                  class="reset-base no-margin !h-[22px] !m-0 !mb-0 !p-0 !px-1 !py-0 !border !border-solid !border-n-brand !rounded !outline-none !bg-n-solid-1 !text-n-slate-12 !text-xs !leading-[20px] !block !w-full box-border !appearance-none"
                  @keydown.enter="saveEdit"
                  @keydown.esc="cancelEditing"
                  @keydown.tab="saveEdit"
                  @blur="saveEdit"
                />
              </div>
            </td>

            <td
              class="py-px px-2 relative z-0 border-r border-n-slate-3 group/cell transition-colors"
            >
              <div
                v-if="!isEditing(item.data.id, 'phoneNumber')"
                class="cursor-text whitespace-nowrap h-[22px] flex items-center"
                @dblclick="
                  startEditing(
                    item.data.id,
                    'phoneNumber',
                    getPhoneNumber(item.data)
                  )
                "
              >
                <span class="text-xs text-n-slate-11 leading-[22px]">{{
                  getPhoneNumber(item.data) || '-'
                }}</span>
              </div>
              <div v-else class="h-[22px]" @click.stop>
                <input
                  v-model="editingCell.value"
                  type="tel"
                  class="reset-base no-margin !h-[22px] !m-0 !mb-0 !p-0 !px-1 !py-0 !border !border-solid !border-n-brand !rounded !outline-none !bg-n-solid-1 !text-n-slate-12 !text-xs !leading-[20px] !block !w-full box-border !appearance-none"
                  @keydown.enter="saveEdit"
                  @keydown.esc="cancelEditing"
                  @keydown.tab="saveEdit"
                  @blur="saveEdit"
                />
              </div>
            </td>

            <td
              class="py-px px-2 relative z-0 border-r border-n-slate-3 transition-colors"
            >
              <template
                v-if="getCountryDetails(getAdditionalAttributes(item.data))"
              >
                <div class="flex items-center gap-1 min-w-0">
                  <Flag
                    :country="
                      getCountryDetails(getAdditionalAttributes(item.data))
                        .countryCode
                    "
                    class="size-3.5 flex-shrink-0"
                  />
                  <span class="text-xs text-n-slate-11 truncate">
                    {{ formatLocation(getAdditionalAttributes(item.data)) }}
                  </span>
                </div>
              </template>
              <span v-else class="text-xs text-n-slate-11">{{
                PLACEHOLDER
              }}</span>
            </td>

            <td
              class="py-px px-2 relative z-0 border-r border-n-slate-3 whitespace-nowrap transition-colors"
            >
              <span class="text-xs text-n-slate-11">
                {{
                  getLastActivityAt(item.data)
                    ? formatDateTime(getLastActivityAt(item.data))
                    : PLACEHOLDER
                }}
              </span>
            </td>

            <td
              class="py-px px-2 relative z-0 border-r border-n-slate-3 whitespace-nowrap transition-colors"
            >
              <span class="text-xs text-n-slate-11">
                {{
                  getLastMessageAt(item.data)
                    ? formatDateTime(getLastMessageAt(item.data))
                    : PLACEHOLDER
                }}
              </span>
            </td>

            <td
              class="py-px px-2 relative z-0 border-r border-n-slate-3 whitespace-nowrap transition-colors"
            >
              <span
                class="text-xs text-n-slate-11 truncate block max-w-[160px]"
                :title="
                  getLastCampaignAt(item.data)
                    ? formatDateTime(getLastCampaignAt(item.data))
                    : ''
                "
              >
                {{
                  getLastCampaignAt(item.data)
                    ? formatDateTime(getLastCampaignAt(item.data))
                    : PLACEHOLDER
                }}
              </span>
            </td>

            <!-- Assigned Agent cell -->
            <td class="py-px px-2 border-r border-n-slate-3 transition-colors">
              <div
                class="flex items-center gap-1.5 cursor-pointer rounded px-1 py-0.5 hover:bg-n-alpha-2 transition-colors min-h-[22px]"
                @click.stop="openAgentDropdown(item.data.id, $event)"
              >
                <template v-if="getAssignedAgent(item.data)">
                  <Avatar
                    :name="getAssignedAgent(item.data).name"
                    :src="
                      getAssignedAgent(item.data).avatar_url ||
                      getAssignedAgent(item.data).avatarUrl
                    "
                    :size="16"
                    rounded-full
                  />
                  <span class="text-xs text-n-slate-12 truncate">
                    {{ getAssignedAgent(item.data).name }}
                  </span>
                </template>
                <template v-else>
                  <Icon
                    icon="i-lucide-user-plus"
                    class="size-3.5 text-n-slate-9"
                  />
                  <span class="text-xs text-n-slate-9">{{ PLACEHOLDER }}</span>
                </template>
              </div>
            </td>

            <td
              v-for="attrKey in visibleCustomColumns"
              :key="attrKey"
              class="py-px px-2 relative z-0 border-r border-n-slate-3 group/cell transition-colors"
            >
              <div
                v-if="!isEditing(item.data.id, attrKey)"
                class="cursor-text h-[22px] flex items-center"
                @dblclick="
                  startEditing(
                    item.data.id,
                    attrKey,
                    getCustomAttributeValue(item.data, attrKey)
                  )
                "
              >
                <span
                  class="text-xs text-n-slate-11 truncate block leading-[22px]"
                >
                  {{ getCustomAttributeValue(item.data, attrKey) }}
                </span>
              </div>
              <div v-else class="h-[22px]" @click.stop>
                <input
                  v-model="editingCell.value"
                  type="text"
                  class="reset-base no-margin !h-[22px] !m-0 !mb-0 !p-0 !px-1 !py-0 !border !border-solid !border-n-brand !rounded !outline-none !bg-n-solid-1 !text-n-slate-12 !text-xs !leading-[20px] !block !w-full box-border !appearance-none"
                  @keydown.enter="saveEdit"
                  @keydown.esc="cancelEditing"
                  @keydown.tab="saveEdit"
                  @blur="saveEdit"
                />
              </div>
            </td>

            <td class="py-px px-2 relative z-0 transition-colors">
              <div class="flex items-center justify-end">
                <Button
                  :label="t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS')"
                  variant="link"
                  size="xs"
                  @click="onClickViewDetails(item.data.id)"
                />
              </div>
            </td>

            <td
              class="py-px px-2 w-[40px] sticky right-0 z-[10] shadow-[-4px_0_8px_rgba(0,0,0,0.03)] transition-colors"
              :class="
                isSelected(item.data.id)
                  ? 'bg-woot-25 dark:bg-woot-800/30'
                  : 'bg-n-solid-1'
              "
            />
          </tr>
        </template>
      </tbody>
    </table>

    <!-- Infinite scroll loader -->
    <div
      v-if="isLoadingMore || isFetchingMore"
      class="flex items-center justify-center py-3 bg-n-slate-1"
    >
      <Spinner size="16" class="!w-4 !h-4" />
    </div>

    <div
      v-if="contacts.length === 0"
      class="flex items-center justify-center py-10"
    >
      <span class="text-sm text-n-slate-11">{{
        t('CONTACTS_LAYOUT.TABLE.NO_CONTACTS')
      }}</span>
    </div>

    <!-- Agent dropdown (teleported to body to escape stacking contexts) -->
    <Teleport to="body">
      <div
        v-if="agentDropdownContactId"
        class="fixed inset-0 z-[9998]"
        @click="closeAgentDropdown"
      />
      <div
        v-if="agentDropdownContactId"
        class="fixed w-56 bg-white rounded-lg shadow-lg border border-n-slate-4 overflow-hidden z-[9999]"
        :style="agentDropdownStyle"
      >
        <div class="px-2 py-1.5 border-b border-n-slate-4">
          <input
            v-model="agentSearchQuery"
            type="text"
            :placeholder="t('CONTACTS_LAYOUT.TABLE.SEARCH_AGENTS_PLACEHOLDER')"
            class="reset-base no-margin w-full px-2 py-1 text-xs bg-n-solid-3 border border-n-weak rounded text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-1 focus:ring-n-brand !h-auto !m-0 !mb-0"
            @click.stop
          />
        </div>
        <div class="max-h-48 overflow-y-auto py-1">
          <button
            type="button"
            class="w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors text-n-slate-11 hover:bg-n-slate-2 hover:text-n-slate-12"
            @click="assignAgent(agentDropdownContactId, null)"
          >
            <Icon
              icon="i-lucide-user-x"
              class="size-3.5 flex-shrink-0 text-n-slate-9"
            />
            <span>{{ t('CONTACTS_LAYOUT.TABLE.UNASSIGNED') }}</span>
          </button>
          <button
            v-for="agent in filteredAgents"
            :key="agent.id"
            type="button"
            class="w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors"
            :class="
              getAssignedAgentIdById(agentDropdownContactId) === agent.id
                ? 'bg-n-slate-3 text-n-slate-12 font-medium'
                : 'text-n-slate-11 hover:bg-n-slate-2 hover:text-n-slate-12'
            "
            @click="assignAgent(agentDropdownContactId, agent.id)"
          >
            <Avatar
              :name="agent.name"
              :src="agent.avatar_url || agent.avatarUrl"
              :size="16"
              rounded-full
              class="flex-shrink-0"
            />
            <span class="truncate flex-1 text-left">{{ agent.name }}</span>
            <Icon
              v-if="getAssignedAgentIdById(agentDropdownContactId) === agent.id"
              icon="i-lucide-check"
              class="size-3.5 text-n-brand flex-shrink-0"
            />
          </button>
          <div
            v-if="filteredAgents.length === 0 && agentSearchQuery"
            class="px-3 py-2 text-xs text-n-slate-10 text-center"
          >
            {{ t('CONTACTS_LAYOUT.TABLE.NO_AGENTS_FOUND') }}
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
